// @ts-check
// Configurações > Papéis (doc 12, seção 8.10): lista com membros por papel e papel padrão; página do papel com as
// permissões gerais, as flags de configuração e ferramenta, a matriz objeto x (ver, editar, excluir), os campos
// ocultos ou somente leitura de cada objeto e as etapas do funil que o papel vê (contrato de equipe, seção 3.2).
import { html } from 'htm/preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { PageCard, PageHeader } from '../page/page-card.js';
import { SettingsSection } from './settings-pages.js';
import { RoleSelect } from './settings-members-page.js';
import { allObjectsState, hasCurrentPermissionFlag } from '../../data/metadata-store.js';
import { deleteRole, listRoles, saveRole, setDefaultRole } from '../../data/team.js';
import { Link, navigate } from '../../app/router.js';
import { NotFoundPage } from '../../app/not-found-page.js';
import { Button } from '../../ui/button.js';
import { Tag } from '../../ui/chip.js';
import { Checkbox, Skeleton, Toggle } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { getFieldIcon, isFieldDisplayable } from '../../lib/field-metadata.js';
import { getSortedOptions } from '../../lib/field-metadata.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import {
  FUNNEL_STAGE_SCOPE_CONFLICTING_FLAGS, LOST_FUNNEL_STAGE, NOT_A_LEAD_FUNNEL_STAGE, SIDE_FUNNEL_STAGES, buildScopeUntilStage, canToggleHandoffAccess, describeFunnelStageScopeLock,
  describeStageAccess, findFunnelStageScopeConflicts, listFunnelSequence, orderStageValues, summarizeFunnelStageScope, toggleStageAccess,
} from '../../../../shared/permissions/funnel-stage-scope.js';

/**
 * @typedef {import('../../data/team.js').Role} Role
 * @typedef {import('../../data/team.js').ObjectPermission} ObjectPermission
 * @typedef {import('../../data/team.js').FieldPermission} FieldPermission
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {'canReadObjectRecords' | 'canUpdateObjectRecords' | 'canSoftDeleteObjectRecords'} ObjectPermissionKey
 */

const NEW_ROLE_ID = 'new';
const GLOBAL_KEYS = /** @type {const} */ ({
  canReadObjectRecords: 'canReadAllObjectRecords',
  canUpdateObjectRecords: 'canUpdateAllObjectRecords',
  canSoftDeleteObjectRecords: 'canSoftDeleteAllObjectRecords',
});
/** @type {ObjectPermissionKey[]} */
const MATRIX_KEYS = ['canReadObjectRecords', 'canUpdateObjectRecords', 'canSoftDeleteObjectRecords'];
const SETTINGS_FLAGS = ['WORKSPACE', 'WORKSPACE_MEMBERS', 'ROLES', 'DATA_MODEL', 'WORKFLOWS', 'API_KEYS_AND_WEBHOOKS', 'SECURITY', 'BILLING'];
// WHATSAPP_CAMPAIGNS (2.0.0): campanhas e comunicados pelo WhatsApp
const TOOL_FLAGS = ['EXPORT_CSV', 'IMPORT_CSV', 'EMAIL_CAMPAIGNS', 'WHATSAPP_CAMPAIGNS'];
// painéis, conversas e relatório de visitas: incluídos quando o papel lê todos os registros
const DATA_ACCESS_FLAGS = ['DASHBOARDS', 'CONVERSATIONS', 'VISIT_REPORTS'];

/** @returns {Role} */
const emptyRole = () => ({
  id: NEW_ROLE_ID, standardKey: null, label: '', description: '', icon: 'IconUser', isEditable: true,
  canUpdateAllSettings: false, canAccessAllTools: false, canReadAllObjectRecords: true, canUpdateAllObjectRecords: true,
  canSoftDeleteAllObjectRecords: false, canDestroyAllObjectRecords: false, canBeAssignedToUsers: true, position: 100,
  permissionFlags: [], objectPermissions: [], fieldPermissions: [], workspaceMemberCount: 0, isDefault: false, funnelStageScope: null,
});

const CARD_OBJECT_NAME = 'enrollmentOpportunity';
const VISIT_SCHEDULED_STAGE = 'VISIT_SCHEDULED';
const VISIT_DONE_STAGE = 'VISIT_DONE';
const LEAD_STAGE = 'LEAD';

/**
 * Opções da etapa do card na ordem do funil (a mesma fonte da tela Funil e etapas).
 * @returns {Array<{ id?: string, value: string, label: string, color?: string, position?: number }>}
 */
function useStageOptions() {
  const card = allObjectsState.value.find((objectMetadata) => objectMetadata.nameSingular === CARD_OBJECT_NAME);
  const stageField = card?.fields.find((field) => field.name === 'stage');
  return stageField ? getSortedOptions(stageField) : [];
}

/** Objetos em que permissões fazem sentido: os de negócio (sistema e membros da equipe seguem regras fixas). */
function useConfigurableObjects() {
  return allObjectsState.value
    .filter((objectMetadata) => !objectMetadata.isSystem && objectMetadata.isActive !== false && objectMetadata.nameSingular !== 'workspaceMember')
    .sort((first, second) => translateMetadataLabel(first.labelPlural).localeCompare(translateMetadataLabel(second.labelPlural)));
}

/**
 * Valor efetivo de uma permissão de objeto: a sobrescrita, se houver, senão a regra geral do papel. Escrita sem
 * leitura não existe.
 * @param {Role} role @param {string} objectMetadataId @param {ObjectPermissionKey} key
 */
export function effectiveObjectPermission(role, objectMetadataId, key) {
  const override = role.objectPermissions.find((permission) => permission.objectMetadataId === objectMetadataId);
  const read = override?.canReadObjectRecords ?? role.canReadAllObjectRecords;
  if (key === 'canReadObjectRecords') return read;
  return read && (override?.[key] ?? role[GLOBAL_KEYS[key]]);
}

/**
 * Nova lista de sobrescritas ao marcar ou desmarcar uma célula da matriz; valores iguais à regra geral não viram
 * sobrescrita, para mudar a regra geral depois continuar valendo para esse objeto.
 * @param {Role} role @param {string} objectMetadataId @param {ObjectPermissionKey} key @param {boolean} value
 * @returns {ObjectPermission[]}
 */
export function toggleObjectPermission(role, objectMetadataId, key, value) {
  const current = role.objectPermissions.find((permission) => permission.objectMetadataId === objectMetadataId)
    ?? { objectMetadataId, canReadObjectRecords: null, canUpdateObjectRecords: null, canSoftDeleteObjectRecords: null, canDestroyObjectRecords: null };
  /** @type {ObjectPermission} */
  const next = { ...current, [key]: value };
  // ler é pré-requisito; tirar a leitura tira as escritas, dar escrita dá leitura
  if (key === 'canReadObjectRecords' && !value) {
    next.canUpdateObjectRecords = false;
    next.canSoftDeleteObjectRecords = false;
    next.canDestroyObjectRecords = false;
  }
  if (key !== 'canReadObjectRecords' && value) next.canReadObjectRecords = true;
  for (const matrixKey of /** @type {(ObjectPermissionKey | 'canDestroyObjectRecords')[]} */ ([...MATRIX_KEYS, 'canDestroyObjectRecords'])) {
    const globalValue = matrixKey === 'canDestroyObjectRecords' ? role.canDestroyAllObjectRecords : role[GLOBAL_KEYS[matrixKey]];
    if (next[matrixKey] === globalValue) next[matrixKey] = null;
  }
  const others = role.objectPermissions.filter((permission) => permission.objectMetadataId !== objectMetadataId);
  const isEmpty = MATRIX_KEYS.every((matrixKey) => next[matrixKey] === null) && next.canDestroyObjectRecords === null;
  return isEmpty ? others : [...others, next];
}

/**
 * @param {Role} role @param {ObjectMetadata} objectMetadata @param {string} fieldMetadataId
 * @param {'canReadFieldValue' | 'canUpdateFieldValue'} key @param {boolean} isAllowed
 * @returns {FieldPermission[]}
 */
export function toggleFieldPermission(role, objectMetadata, fieldMetadataId, key, isAllowed) {
  const current = role.fieldPermissions.find((permission) => permission.fieldMetadataId === fieldMetadataId)
    ?? { objectMetadataId: objectMetadata.id, fieldMetadataId, canReadFieldValue: null, canUpdateFieldValue: null };
  /** @type {FieldPermission} */
  const next = { ...current, [key]: isAllowed ? null : false };
  if (key === 'canReadFieldValue' && !isAllowed) next.canUpdateFieldValue = false;
  if (key === 'canUpdateFieldValue' && isAllowed) next.canReadFieldValue = null;
  const others = role.fieldPermissions.filter((permission) => permission.fieldMetadataId !== fieldMetadataId);
  return next.canReadFieldValue === null && next.canUpdateFieldValue === null ? others : [...others, next];
}

export function SettingsRolesPage() {
  const [data, setData] = useState(/** @type {{ roles: Role[], defaultRoleId: string } | null} */ (null));
  const stageOptions = useStageOptions();
  const canManageRoles = hasCurrentPermissionFlag('ROLES');
  const load = () => listRoles().then(setData).catch((error) => {
    showErrorSnackbar(error);
    setData({ roles: [], defaultRoleId: '' });
  });
  useEffect(() => {
    document.title = `${t('team.roles.title')} - ${t('navigation.settings')}`;
    load();
  }, []);

  const changeDefault = async (/** @type {string} */ roleId) => {
    try {
      await setDefaultRole(roleId);
      showSuccessSnackbar(t('team.roles.defaultChanged'));
      load();
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  return html`<${PageCard} header=${html`<${PageHeader} icon="IconShieldLock" title=${t('team.roles.title')}
    actions=${canManageRoles ? html`<${Button} icon="IconPlus" title=${t('team.roles.create')} size="small" accent="blue" onClick=${() => navigate(`/settings/roles/${NEW_ROLE_ID}`)} />` : null} />`}>
    <div class="settings-page">
      <${SettingsSection} title=${t('team.roles.listTitle')} description=${t('team.roles.description')}>
        ${data
          ? html`<div class="settings-table team-table">
              <div class="settings-table-row settings-table-header team-row" data-columns="roles">
                <span>${t('team.roles.columns.name')}</span>
                <span class="settings-table-number">${t('team.roles.columns.members')}</span>
                <span></span>
              </div>
              ${data.roles.map((role) => html`<${Link} key=${role.id} href=${`/settings/roles/${role.id}`} className="settings-table-row team-row" data-columns="roles">
                <span class="team-role-name">
                  <${Icon} name=${role.icon ?? 'IconUser'} size=${16} />
                  <span class="team-role-texts">
                    <span class="team-role-label">${role.label}${role.isDefault ? html` <${Tag} color="blue" text=${t('team.roles.defaultTag')} />` : null}${!role.isEditable ? html` <${Tag} color="gray" text=${t('team.roles.lockedTag')} />` : null}</span>
                    ${role.description ? html`<span class="team-role-description">${role.description}</span>` : null}
                    ${role.funnelStageScope ? html`<span class="team-role-description" data-testid="role-stage-summary">
                      <${Icon} name="IconFilter" size=${12} /> ${t('team.roles.funnelStages.summarySelected', summarizeFunnelStageScope(toScopeValues(role.funnelStageScope, stageOptions), stageOptions))}
                    </span>` : null}
                  </span>
                </span>
                <span class="settings-table-number">${role.workspaceMemberCount}</span>
                <span class="settings-table-chevron"><${Icon} name="IconChevronRight" size=${16} /></span>
              <//>`)}
            </div>`
          : html`<${Skeleton} height=${160} radius=${8} />`}
      <//>
      ${data && canManageRoles
        ? html`<${SettingsSection} title=${t('team.roles.defaultTitle')} description=${t('team.roles.defaultDescription')}>
            <div class="team-default-role">
              <${RoleSelect} roles=${data.roles} value=${data.defaultRoleId} onChange=${changeDefault} ariaLabel=${t('team.roles.defaultTitle')} />
            </div>
          <//>`
        : null}
    </div>
  <//>`;
}

/**
 * @param {{ role: Role, objectMetadata: ObjectMetadata, isDisabled: boolean, onChange: (fieldPermissions: FieldPermission[]) => void }} props
 */
function FieldPermissionList({ role, objectMetadata, isDisabled, onChange }) {
  const fields = objectMetadata.fields
    .filter((field) => isFieldDisplayable(field) && !field.isSystem && field.id !== objectMetadata.labelIdentifierFieldMetadataId)
    .sort((first, second) => (first.position ?? 0) - (second.position ?? 0));
  const canUpdateObject = effectiveObjectPermission(role, objectMetadata.id, 'canUpdateObjectRecords');
  return html`<div class="team-field-list">
    <div class="team-field-row team-field-header">
      <span>${t('team.roles.fields.field')}</span>
      <span>${t('team.roles.fields.see')}</span>
      <span>${t('team.roles.fields.edit')}</span>
    </div>
    ${fields.map((field) => {
      const permission = role.fieldPermissions.find((candidate) => candidate.fieldMetadataId === field.id);
      const canSee = permission?.canReadFieldValue !== false;
      const canEdit = canSee && permission?.canUpdateFieldValue !== false;
      const label = translateMetadataLabel(field.label);
      return html`<div key=${field.id} class="team-field-row">
        <span class="settings-table-name"><${Icon} name=${getFieldIcon(field)} size=${16} />${label}</span>
        <span><${Checkbox} checked=${canSee} disabled=${isDisabled} ariaLabel=${t('team.roles.fields.seeField', { field: label })}
          onChange=${(checked) => onChange(toggleFieldPermission(role, objectMetadata, field.id, 'canReadFieldValue', checked))} /></span>
        <span><${Checkbox} checked=${canEdit && canUpdateObject} disabled=${isDisabled || !canUpdateObject} ariaLabel=${t('team.roles.fields.editField', { field: label })}
          onChange=${(checked) => onChange(toggleFieldPermission(role, objectMetadata, field.id, 'canUpdateFieldValue', checked))} /></span>
      </div>`;
    })}
  </div>`;
}

/**
 * @param {{ role: Role, isDisabled: boolean, onChange: (patch: Partial<Role>) => void }} props
 */
function ObjectPermissionMatrix({ role, isDisabled, onChange }) {
  const objects = useConfigurableObjects();
  const [expandedId, setExpandedId] = useState(/** @type {string | null} */ (null));
  return html`<div class="settings-table team-table team-matrix">
    <div class="settings-table-row settings-table-header team-row" data-columns="matrix">
      <span>${t('team.roles.matrix.object')}</span>
      ${MATRIX_KEYS.map((key) => html`<span key=${key} class="team-matrix-cell">${t(`team.roles.matrix.${key}`)}</span>`)}
      <span>${t('team.roles.matrix.fields')}</span>
    </div>
    ${objects.map((objectMetadata) => {
      const objectLabel = translateMetadataLabel(objectMetadata.labelPlural);
      const restrictions = role.fieldPermissions.filter((permission) => permission.objectMetadataId === objectMetadata.id).length;
      const isExpanded = expandedId === objectMetadata.id;
      const canRead = effectiveObjectPermission(role, objectMetadata.id, 'canReadObjectRecords');
      return html`<div key=${objectMetadata.id} class="team-matrix-group">
        <div class="settings-table-row team-row" data-columns="matrix">
          <span class="settings-table-name"><${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${16} />${objectLabel}</span>
          ${MATRIX_KEYS.map((key) => html`<span key=${key} class="team-matrix-cell">
            <${Checkbox} checked=${effectiveObjectPermission(role, objectMetadata.id, key)} disabled=${isDisabled}
              ariaLabel=${`${t(`team.roles.matrix.${key}`)}: ${objectLabel}`}
              onChange=${(checked) => onChange({ objectPermissions: toggleObjectPermission(role, objectMetadata.id, key, checked) })} />
          </span>`)}
          <span>
            <button type="button" class="team-fields-toggle" disabled=${!canRead} aria-expanded=${isExpanded ? 'true' : 'false'}
              onClick=${() => setExpandedId(isExpanded ? null : objectMetadata.id)}>
              ${restrictions > 0 ? t('team.roles.matrix.restrictions', { count: restrictions }) : t('team.roles.matrix.allFields')}
              <${Icon} name=${isExpanded ? 'IconChevronUp' : 'IconChevronDown'} size=${14} />
            </button>
          </span>
        </div>
        ${isExpanded
          ? html`<${FieldPermissionList} role=${role} objectMetadata=${objectMetadata} isDisabled=${isDisabled}
              onChange=${(fieldPermissions) => onChange({ fieldPermissions })} />`
          : null}
      </div>`;
    })}
  </div>`;
}

/**
 * Escopo do papel só com valores (o que a API grava e devolve), na ordem do funil.
 * @param {Role['funnelStageScope']} scope @param {Array<{ value: string }>} options
 * @returns {import('../../../../shared/permissions/funnel-stage-scope.js').FunnelStageScopeValues | null}
 */
export function toScopeValues(scope, options) {
  if (!scope) return null;
  return {
    visibleStageValues: orderStageValues(options, scope.visibleStageValues ?? []),
    handoffStageValues: orderStageValues(options, scope.handoffStageValues ?? []),
  };
}

/**
 * Seção "Etapas do funil" do papel: todas as etapas ou só as marcadas, com Ver e Pode passar por etapa.
 * @param {{ role: Role, isDisabled: boolean, onChange: (patch: Partial<Role>) => void }} props
 */
function FunnelStageScopeSection({ role, isDisabled, onChange }) {
  const options = useStageOptions();
  const [untilApplied, setUntilApplied] = useState(/** @type {{ first: string, last: string, withHandoff: boolean } | null} */ (null));
  if (options.length === 0) return null;
  const scope = toScopeValues(role.funnelStageScope, options);
  const conflicts = findFunnelStageScopeConflicts(role);
  // o Administrador e os papéis com todas as configurações veem todas as etapas
  const allStagesLock = describeFunnelStageScopeLock(role);
  const isLocked = isDisabled || Boolean(allStagesLock) || (!scope && conflicts.length > 0);
  const sequence = listFunnelSequence(options);
  const sideStages = options.filter((option) => SIDE_FUNNEL_STAGES.includes(option.value));
  const canUpdateCards = effectiveObjectPermission(role, cardObjectId(), 'canUpdateObjectRecords');
  // o mesmo nome do objeto que a matriz de Permissões por objeto mostra
  const handoffNeedsUpdateText = t('team.roles.funnelStages.handoffNeedsUpdate', { object: cardObjectLabel() });
  const summary = summarizeFunnelStageScope(scope, options);
  const labelOf = (/** @type {string} */ value) => translateMetadataLabel(options.find((option) => option.value === value)?.label ?? value);
  const visitDone = options.find((option) => option.value === VISIT_DONE_STAGE);
  const usesConversations = role.canReadAllObjectRecords || role.permissionFlags.includes('CONVERSATIONS');

  /** @param {import('../../../../shared/permissions/funnel-stage-scope.js').FunnelStageScopeValues | null} next */
  const setScope = (next) => onChange({ funnelStageScope: next ? { ...next } : null });
  const setMode = (/** @type {'ALL' | 'SELECTED'} */ mode) => {
    setUntilApplied(null);
    // ao restringir, tudo começa em Ver: nada muda até a pessoa desmarcar
    setScope(mode === 'ALL' ? null : { visibleStageValues: options.map((option) => option.value), handoffStageValues: [] });
  };
  const applyUntil = (/** @type {string} */ value) => {
    if (!value) return;
    const next = buildScopeUntilStage(options, value, { canHandoff: canUpdateCards });
    setScope(next);
    const index = sequence.findIndex((option) => option.value === value);
    setUntilApplied({ first: labelOf(sequence[0]?.value ?? value), last: labelOf(sequence[index]?.value ?? value), withHandoff: next.handoffStageValues.length > 0 });
  };

  // por que a caixa Pode passar da etapa está travada (a matriz tem três estados e depende dessas caixas)
  const handoffDisabledHint = (/** @type {import('../../../../shared/permissions/funnel-stage-scope.js').StageAccess} */ access) => {
    if (isLocked || !scope) return undefined;
    if (access === 'VIEW') return t('team.roles.funnelStages.handoffDisabledByView');
    return canToggleHandoffAccess(access, canUpdateCards) ? undefined : handoffNeedsUpdateText;
  };

  /** @param {{ value: string, label: string, color?: string }} option @param {string | null} hint */
  const stageRow = (option, hint = null) => {
    const access = describeStageAccess(scope, option.value);
    const label = translateMetadataLabel(option.label);
    return html`<div key=${option.value} class="team-stage-row" data-access=${access}>
      <span class="team-stage-name">
        <${Tag} color=${option.color ?? 'gray'} text=${label} />
        ${hint ? html`<span class="team-stage-hint">${hint}</span>` : null}
      </span>
      <span class="team-matrix-cell">
        <${Checkbox} checked=${access === 'VIEW'} disabled=${isLocked || !scope} ariaLabel=${`${t('team.roles.funnelStages.columnView')}: ${label}`}
          onChange=${() => scope && setScope(toggleStageAccess(scope, option.value, 'VIEW'))} />
      </span>
      <span class="team-matrix-cell">
        <${Checkbox} checked=${access === 'HANDOFF'} disabled=${isLocked || !scope || !canToggleHandoffAccess(access, canUpdateCards)}
          ariaLabel=${`${t('team.roles.funnelStages.columnHandoff')}: ${label}`} title=${handoffDisabledHint(access)}
          onChange=${() => scope && setScope(toggleStageAccess(scope, option.value, 'HANDOFF'))} />
      </span>
    </div>`;
  };

  return html`<${SettingsSection} title=${t('team.roles.funnelStages.title')} description=${t('team.roles.funnelStages.description')}>
    <div class="team-stage-scope" data-testid="funnel-stage-scope">
      ${allStagesLock
        ? html`<p class="team-note" data-testid="funnel-stage-lock"><${Icon} name="IconLock" size=${14} />${t(allStagesLock === 'ADMINISTRATOR'
          ? 'team.roles.funnelStages.administratorLocked'
          : 'team.roles.funnelStages.allSettingsLocked')}</p>`
        : null}
      ${!allStagesLock && !scope && conflicts.length > 0
        ? html`<p class="team-note"><${Icon} name="IconLock" size=${14} />${t('team.roles.funnelStages.flagConflict', { flags: conflicts.map((flag) => t(`team.roles.flags.${flag}.label`)).join(', ') })}</p>`
        : null}
      <div class="settings-radio-cards" role="radiogroup" aria-label=${t('team.roles.funnelStages.title')}>
        ${(/** @type {const} */ (['ALL', 'SELECTED'])).map((mode) => html`<label key=${mode} class="settings-radio-card" data-selected=${(mode === 'ALL') === !scope ? 'true' : undefined}
          data-disabled=${isLocked && (mode === 'ALL') !== !scope ? 'true' : undefined}>
          <input type="radio" name=${`funnel-stage-mode-${role.id}`} value=${mode} checked=${(mode === 'ALL') === !scope} disabled=${isLocked}
            onChange=${() => setMode(mode)} />
          <${Icon} name=${mode === 'ALL' ? 'IconLayoutKanban' : 'IconFilter'} size=${18} />
          <span class="settings-radio-card-texts">
            <span class="settings-radio-card-title">${t(mode === 'ALL' ? 'team.roles.funnelStages.modeAll' : 'team.roles.funnelStages.modeSelected')}</span>
          </span>
        </label>`)}
      </div>
      ${scope
        ? html`
          <label class="form-field team-stage-until">
            <span class="form-field-label">${t('team.roles.funnelStages.untilLabel')}</span>
            <select class="text-input" value="" disabled=${isLocked} data-testid="funnel-stage-until"
              onChange=${(/** @type {Event} */ event) => applyUntil(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
              <option value="">${t('team.roles.funnelStages.untilPlaceholder')}</option>
              ${sequence.map((option) => html`<option key=${option.value} value=${option.value}>${translateMetadataLabel(option.label)}</option>`)}
            </select>
          </label>
          ${untilApplied
            ? html`<span class="input-hint">${t(untilApplied.withHandoff ? 'team.roles.funnelStages.untilApplied' : 'team.roles.funnelStages.untilAppliedNoHandoff', untilApplied)}</span>`
            : null}
          <div class="settings-table team-table team-stage-table">
            <div class="team-stage-row team-stage-header">
              <span>${t('team.roles.funnelStages.columnStage')}</span>
              <span class="team-matrix-cell">${t('team.roles.funnelStages.columnView')}</span>
              <span class="team-matrix-cell">${t('team.roles.funnelStages.columnHandoff')}</span>
            </div>
            ${sequence.map((option) => stageRow(option))}
            ${sideStages.length > 0 ? html`<div class="team-stage-group">${t('team.roles.funnelStages.sideStagesTitle')}</div>` : null}
            ${sideStages.map((option) => stageRow(option, option.value === LOST_FUNNEL_STAGE ? t('team.roles.funnelStages.lostHint') : option.value === NOT_A_LEAD_FUNNEL_STAGE ? t('team.roles.funnelStages.notLeadHint') : null))}
          </div>
          <span class="input-hint" data-testid="funnel-stage-summary">${t('team.roles.funnelStages.summarySelected', { count: summary.count, total: summary.total })}. ${t('team.roles.funnelStages.handoffHint')}</span>
          <ul class="team-stage-tips">
            ${role.isDefault ? html`<li data-tone="warning">${t('team.roles.funnelStages.defaultRoleWarning')}</li>` : null}
            ${visitDone && scope.visibleStageValues.includes(VISIT_SCHEDULED_STAGE) && describeStageAccess(scope, VISIT_DONE_STAGE) !== 'VIEW'
              ? html`<li>${t('team.roles.funnelStages.visitDoneTip', { stage: translateMetadataLabel(visitDone.label) })}</li>`
              : null}
            ${usesConversations && !scope.visibleStageValues.includes(LEAD_STAGE) ? html`<li>${t('team.roles.funnelStages.leadHint')}</li>` : null}
            ${!canUpdateCards ? html`<li>${handoffNeedsUpdateText}</li>` : null}
            <li>${t('team.roles.funnelStages.familyNotesNote')}</li>
            <li>${t('team.roles.funnelStages.newStagesNote')}</li>
          </ul>`
        : html`<span class="input-hint" data-testid="funnel-stage-summary">${t('team.roles.funnelStages.summaryAll')}</span>`}
    </div>
  <//>`;
}

/** Id do objeto do card (para a permissão de edição que o Pode passar exige). */
function cardObjectId() {
  return allObjectsState.value.find((objectMetadata) => objectMetadata.nameSingular === CARD_OBJECT_NAME)?.id ?? '';
}

/** Nome do objeto do card como a matriz de Permissões por objeto o mostra (a escola pode renomear). */
function cardObjectLabel() {
  const cardObject = allObjectsState.value.find((objectMetadata) => objectMetadata.nameSingular === CARD_OBJECT_NAME);
  return cardObject ? translateMetadataLabel(cardObject.labelPlural) : t('team.roles.funnelStages.cardObjectFallback');
}

/**
 * @param {{ label: string, description?: string, value: boolean, disabled: boolean, onChange: (value: boolean) => void }} props
 */
function ToggleRow({ label, description, value, disabled, onChange }) {
  return html`<div class="team-toggle-row" data-disabled=${disabled ? 'true' : undefined}>
    <span class="team-toggle-texts">
      <span class="team-toggle-label">${label}</span>
      ${description ? html`<span class="team-toggle-description">${description}</span>` : null}
    </span>
    <${Toggle} value=${value} ariaLabel=${label} onChange=${disabled ? undefined : onChange} />
  </div>`;
}

/**
 * @param {{ roleId: string }} props
 */
export function SettingsRoleDetailPage({ roleId }) {
  const isNew = roleId === NEW_ROLE_ID;
  const canManageRoles = hasCurrentPermissionFlag('ROLES');
  const [role, setRole] = useState(/** @type {Role | null | undefined} */ (isNew ? emptyRole() : undefined));
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [defaultRoleLabel, setDefaultRoleLabel] = useState('');

  useEffect(() => {
    listRoles()
      .then((data) => {
        setDefaultRoleLabel(data.roles.find((candidate) => candidate.id === data.defaultRoleId)?.label ?? '');
        if (!isNew) setRole(data.roles.find((candidate) => candidate.id === roleId) ?? null);
      })
      .catch((error) => {
        showErrorSnackbar(error);
        if (!isNew) setRole(null);
      });
  }, [roleId]);

  useEffect(() => {
    if (role) document.title = `${role.label || t('team.roles.newRole')} - ${t('team.roles.title')}`;
  }, [role?.label]);

  const flagsCovered = useMemo(() => ({
    settings: Boolean(role?.canUpdateAllSettings),
    tools: Boolean(role?.canAccessAllTools),
  }), [role?.canUpdateAllSettings, role?.canAccessAllTools]);

  if (role === undefined) return html`<${PageCard} header=${html`<${PageHeader} icon="IconShieldLock" title=${t('team.roles.title')} />`}><div class="settings-page"><${Skeleton} height=${240} radius=${8} /></div><//>`;
  if (role === null) return html`<${NotFoundPage} />`;

  const isDisabled = !canManageRoles || !role.isEditable;
  const hasScope = Boolean(role.funnelStageScope);
  /** @param {Partial<Role>} patch */
  const change = (patch) => {
    setRole({ ...role, ...patch });
    setIsDirty(true);
  };

  const save = async () => {
    if (!role.label.trim()) {
      showErrorSnackbar(new Error(t('team.roles.labelRequired')));
      return;
    }
    setIsSaving(true);
    try {
      const saved = await saveRole(isNew ? null : role.id, {
        label: role.label.trim(),
        description: role.description,
        icon: role.icon,
        canUpdateAllSettings: role.canUpdateAllSettings,
        canAccessAllTools: role.canAccessAllTools,
        canReadAllObjectRecords: role.canReadAllObjectRecords,
        canUpdateAllObjectRecords: role.canUpdateAllObjectRecords,
        canSoftDeleteAllObjectRecords: role.canSoftDeleteAllObjectRecords,
        canDestroyAllObjectRecords: role.canDestroyAllObjectRecords,
        permissionFlags: role.permissionFlags,
        objectPermissions: role.objectPermissions,
        fieldPermissions: role.fieldPermissions,
        funnelStageScope: role.funnelStageScope
          ? { visibleStageValues: role.funnelStageScope.visibleStageValues, handoffStageValues: role.funnelStageScope.handoffStageValues }
          : null,
      });
      showSuccessSnackbar(t('team.roles.saved'));
      setIsDirty(false);
      if (isNew && saved) navigate(`/settings/roles/${saved.id}`, { replace: true });
      else if (saved) setRole(saved);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteRole(role.id);
      showSuccessSnackbar(t('team.roles.deleted'));
      navigate('/settings/roles', { replace: true });
    } catch (error) {
      showErrorSnackbar(error);
      setIsDeleteOpen(false);
    }
  };

  /** @param {string} flag @param {boolean} value */
  const toggleFlag = (flag, value) => change({
    permissionFlags: value ? [...new Set([...role.permissionFlags, flag])] : role.permissionFlags.filter((candidate) => candidate !== flag),
  });

  /** @param {'canReadAllObjectRecords' | 'canUpdateAllObjectRecords' | 'canSoftDeleteAllObjectRecords' | 'canDestroyAllObjectRecords'} key @param {boolean} value */
  const toggleGlobal = (key, value) => {
    /** @type {Partial<Role>} */
    const patch = { [key]: value };
    if (key === 'canReadAllObjectRecords' && !value) Object.assign(patch, { canUpdateAllObjectRecords: false, canSoftDeleteAllObjectRecords: false, canDestroyAllObjectRecords: false });
    if (key !== 'canReadAllObjectRecords' && value) patch.canReadAllObjectRecords = true;
    change(patch);
  };

  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href="/settings/roles" className="record-breadcrumb-prefix">${t('team.roles.title')}<//>
    <span class="record-breadcrumb-separator">/</span>
    <span class="record-breadcrumb-current">${role.label || t('team.roles.newRole')}</span>
  </nav>`;
  const actions = canManageRoles && role.isEditable
    ? html`
        ${!isNew && !role.isDefault ? html`<${Button} icon="IconTrash" title=${t('team.roles.delete')} size="small" variant="secondary" accent="danger" onClick=${() => setIsDeleteOpen(true)} />` : null}
        <${Button} icon="IconDeviceFloppy" title=${t('common.save')} size="small" accent="blue" isLoading=${isSaving} disabled=${!isDirty && !isNew} onClick=${save} />`
    : null;

  return html`<${PageCard} header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${actions} />`}>
    <div class="settings-page team-role-page">
      ${!role.isEditable ? html`<p class="team-note"><${Icon} name="IconLock" size=${14} />${t('team.roles.notEditable')}</p>` : null}
      <${SettingsSection} title=${t('team.roles.identity')}>
        <div class="team-role-identity">
          <label class="form-field">
            <span class="form-field-label">${t('team.roles.label')}</span>
            <input class="text-input" type="text" value=${role.label} maxlength="60" disabled=${isDisabled} placeholder=${t('team.roles.labelPlaceholder')}
              onInput=${(event) => change({ label: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
          </label>
          <label class="form-field">
            <span class="form-field-label">${t('team.roles.descriptionLabel')}</span>
            <input class="text-input" type="text" value=${role.description ?? ''} maxlength="300" disabled=${isDisabled}
              onInput=${(event) => change({ description: /** @type {HTMLInputElement} */ (event.currentTarget).value })} />
          </label>
        </div>
        ${!isNew ? html`<span class="input-hint">${t('team.roles.membersCount', { count: role.workspaceMemberCount })}</span>` : null}
      <//>
      <${SettingsSection} title=${t('team.roles.allObjects')} description=${t('team.roles.allObjectsDescription')}>
        <div class="team-toggle-list">
          <${ToggleRow} label=${t('team.roles.global.canReadAllObjectRecords')} value=${role.canReadAllObjectRecords} disabled=${isDisabled} onChange=${(value) => toggleGlobal('canReadAllObjectRecords', value)} />
          <${ToggleRow} label=${t('team.roles.global.canUpdateAllObjectRecords')} value=${role.canUpdateAllObjectRecords} disabled=${isDisabled} onChange=${(value) => toggleGlobal('canUpdateAllObjectRecords', value)} />
          <${ToggleRow} label=${t('team.roles.global.canSoftDeleteAllObjectRecords')} value=${role.canSoftDeleteAllObjectRecords} disabled=${isDisabled} onChange=${(value) => toggleGlobal('canSoftDeleteAllObjectRecords', value)} />
          <${ToggleRow} label=${t('team.roles.global.canDestroyAllObjectRecords')} description=${t('team.roles.global.canDestroyAllObjectRecordsDescription')} value=${role.canDestroyAllObjectRecords} disabled=${isDisabled} onChange=${(value) => toggleGlobal('canDestroyAllObjectRecords', value)} />
        </div>
      <//>
      <${SettingsSection} title=${t('team.roles.objectsTitle')} description=${t('team.roles.objectsDescription')}>
        <${ObjectPermissionMatrix} role=${role} isDisabled=${isDisabled} onChange=${change} />
      <//>
      <${FunnelStageScopeSection} role=${role} isDisabled=${isDisabled} onChange=${change} />
      <${SettingsSection} title=${t('team.roles.settingsTitle')} description=${t('team.roles.settingsDescription')}>
        <div class="team-toggle-list">
          <${ToggleRow} label=${t('team.roles.global.canUpdateAllSettings')}
            description=${hasScope ? t('team.roles.funnelStages.flagLockedByScope') : t('team.roles.global.canUpdateAllSettingsDescription')}
            value=${role.canUpdateAllSettings} disabled=${isDisabled || (hasScope && !role.canUpdateAllSettings)} onChange=${(value) => change({ canUpdateAllSettings: value })} />
          ${SETTINGS_FLAGS.map((flag) => {
            // com etapas restritas, as flags que agem como o sistema ficam travadas (contrato de equipe, seção 3.2)
            const isLockedByScope = hasScope && FUNNEL_STAGE_SCOPE_CONFLICTING_FLAGS.includes(flag) && !role.permissionFlags.includes(flag);
            return html`<${ToggleRow} key=${flag} label=${t(`team.roles.flags.${flag}.label`)}
              description=${isLockedByScope ? t('team.roles.funnelStages.flagLockedByScope') : t(`team.roles.flags.${flag}.description`)}
              value=${flagsCovered.settings || role.permissionFlags.includes(flag)} disabled=${isDisabled || flagsCovered.settings || isLockedByScope} onChange=${(value) => toggleFlag(flag, value)} />`;
          })}
        </div>
      <//>
      <${SettingsSection} title=${t('team.roles.toolsTitle')} description=${t('team.roles.toolsDescription')}>
        <div class="team-toggle-list">
          <${ToggleRow} label=${t('team.roles.global.canAccessAllTools')} value=${role.canAccessAllTools} disabled=${isDisabled} onChange=${(value) => change({ canAccessAllTools: value })} />
          ${TOOL_FLAGS.map((flag) => html`<${ToggleRow} key=${flag} label=${t(`team.roles.flags.${flag}.label`)} description=${t(`team.roles.flags.${flag}.description`)}
            value=${flagsCovered.tools || role.permissionFlags.includes(flag)} disabled=${isDisabled || flagsCovered.tools} onChange=${(value) => toggleFlag(flag, value)} />`)}
        </div>
      <//>
      <${SettingsSection} title=${t('team.roles.accessTitle')} description=${t('team.roles.accessDescription')}>
        <div class="team-toggle-list">
          ${DATA_ACCESS_FLAGS.map((flag) => html`<${ToggleRow} key=${flag} label=${t(`team.roles.flags.${flag}.label`)} description=${t(`team.roles.flags.${flag}.description`)}
            value=${role.canReadAllObjectRecords || role.permissionFlags.includes(flag)} disabled=${isDisabled || role.canReadAllObjectRecords} onChange=${(value) => toggleFlag(flag, value)} />`)}
        </div>
      <//>
    </div>
    ${isDeleteOpen
      ? html`<${Modal}
          title=${t('team.roles.deleteTitle', { role: role.label })}
          subtitle=${t('team.roles.deleteWarning', { role: defaultRoleLabel })}
          confirmLabel=${t('team.roles.delete')}
          confirmAccent="danger"
          onClose=${() => setIsDeleteOpen(false)}
          onConfirm=${confirmDelete}
        />`
      : null}
  <//>`;
}
